import { Translate, translateDateFormat } from '../../../../../../../shared/LanguageService';
import { ImageVersion } from '../queries/use-query-edited-image';

export enum VersionKind {
    Original = 'original',
    Edited = 'edited'
}

export type ImageVersionTab = {
    id: number;
    kind: VersionKind;
    label: string;
    version: ImageVersion;
};

export function formatVersionLabel(time: string): string {
    return translateDateFormat(new Date(time), 'P p');
}

export function getImageVersionTabs(versions: ImageVersion[]): ImageVersionTab[] {
    let firstOriginal = versions.find((version) => version.parentId === null);
    let editedVersions = versions.filter((version) => version.parentId !== null);
    let tabs: ImageVersionTab[] = [];

    if (firstOriginal) {
        tabs.push({
            id: firstOriginal.id,
            kind: VersionKind.Original,
            label: Translate('image-processing/modal/button-label/original'),
            version: firstOriginal
        });
    }

    editedVersions.forEach((version) =>
        tabs.push({
            id: version.id,
            kind: VersionKind.Edited,
            label: formatVersionLabel(version.time),
            version
        })
    );

    return tabs;
}

export function getDefaultVersionId(versions: ImageVersion[]): number | null {
    if (versions.length === 0) {
        return null;
    }

    return versions[versions.length - 1].id;
}

const BYTES_PER_MEGABYTE = 1024 * 1024;

export function formatImageDetails(version: ImageVersion): string {
    let sizeInMegabytes = version.fileSize
        ? `${(version.fileSize / BYTES_PER_MEGABYTE).toFixed(2)} MB`
        : 'N/A';

    if (version.filename.toLowerCase().endsWith('.dng')) {
        return sizeInMegabytes;
    }

    let sizeInPixels =
        version.width && version.height ? `${version.width} x ${version.height} px` : 'N/A';
    let dpi = version.dpi > 0 ? `${version.dpi} DPI` : 'N/A';

    return `${sizeInPixels}, ${sizeInMegabytes}, ${dpi}`;
}

export function formatImagePosition(imageIds: number[], imageId: number | null): string {
    let index = imageId === null ? -1 : imageIds.indexOf(imageId);

    if (index === -1) {
        return '';
    }

    return `(${index + 1}/${imageIds.length})`;
}

export enum VersionStatusIcon {
    Comments = 'comments',
    NeedsEditing = 'needs-editing'
}

export type VersionStatus = {
    icon: VersionStatusIcon;
    title: string;
};

export function getVersionStatuses(version: ImageVersion, isLatest: boolean): VersionStatus[] {
    let statuses: VersionStatus[] = [];

    if (version.hasComments) {
        statuses.push({
            icon: VersionStatusIcon.Comments,
            title: Translate(
                isLatest
                    ? 'order-status/has-new-comments'
                    : 'image-processing/page/list/thumbnail/icons/has-old-comments'
            )
        });
    }

    if (version.needsEditing) {
        statuses.push({
            icon: VersionStatusIcon.NeedsEditing,
            title: Translate(
                isLatest ? 'order-status/needs-editing' : 'order-status/returned-from-needs-editing'
            )
        });
    }

    return statuses;
}
